<script setup lang="ts">
  import { reactive, computed, onMounted, watch } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { UiModalHeader, SelectInput, ColoredButton, ThemeProvider } from '@/shared/ui';
  import { usePositionOptions } from '../lib';
  import type { Position } from '@/entities/Position';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';

  const props = defineProps<{
    title?: string;
    position: Position | null;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'save', data: { sourcePositionId: number }): void;
  }>();

  const { positionOptions, isLoading: isLoadingPositions, loadPositions } = usePositionOptions();

  const formData = reactive({
    sourcePositionId: '' as string | number,
  });

  const filteredPositionOptions = computed(() => {
    if (!props.position) return positionOptions.value;
    return positionOptions.value.filter((opt) => opt.id !== props.position?.id?.toString());
  });

  const isFormValid = computed(() => {
    return !!formData.sourcePositionId;
  });

  const handleClose = () => {
    emit('close');
  };

  const handleSave = () => {
    if (!isFormValid.value) return;

    emit('save', {
      sourcePositionId: Number(formData.sourcePositionId),
    });
  };

  const handleClosed = () => {
    formData.sourcePositionId = '';
  };

  watch(
    () => props.position,
    () => {
      formData.sourcePositionId = '';
    }
  );

  onMounted(() => {
    loadPositions();
  });
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[1000px] shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @closed="handleClosed"
  >
    <ThemeProvider class="flex flex-col">
      <div class="flex flex-col">
        <UiModalHeader
          class="px-6 py-4 text-sm border-b border-gray-02 flex-shrink-0"
          :title="title || 'Clonar Permissões'"
          @close="handleClose"
        />

        <div class="px-6 py-6 space-y-4">
          <div class="flex flex-col gap-1">
            <p class="text-sm text-gray-07 font-semibold">
              Clonar permissões do Cargo:
            </p>
            <p class="text-xs text-gray-05">
              Ao clonar permissões de outro cargo, todas as permissões concedidas anteriormente
              serão removidas para incluir as novas
            </p>
          </div>

          <div class="flex flex-col gap-2">
            <SelectInput
              v-model="formData.sourcePositionId"
              :options="filteredPositionOptions"
              :disabled="isLoadingPositions"
              :label="isLoadingPositions ? 'Carregando...' : 'Selecione um Cargo'"
              container-class="w-auto max-w-xs"
              max-height="110px"
            />
          </div>
        </div>

        <div class="px-6 py-8 flex justify-end gap-3 border-t border-gray-02 flex-shrink-0">
          <ColoredButton
            text="Cancelar"
            outlined
            color="gray-03"
            @click="handleClose"
          />
          <ColoredButton
            :disabled="!isFormValid"
            text="Salvar"
            :icon="CheckMarkIcon"
            @click="handleSave"
          />
        </div>
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>

<style scoped>
  .vfm__overlay {
    background-color: rgba(0, 0, 0, 0.5);
  }
</style>
